/** Task & Action Management — pure helpers (filters, dates, selection). */
import { OPEN_STATUSES, PRIORITY_META, categoryGroup } from './constants'
import type { FilterId } from './constants'
import type { ActionTask as Task } from './types'

const DAY = 86_400_000

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/** Start of the calendar week (Monday) containing the given day. */
function startOfWeek(ms: number) {
  const d = new Date(ms)
  const dow = (d.getDay() + 6) % 7 // Mon=0 .. Sun=6
  return startOfDay(new Date(d.getFullYear(), d.getMonth(), d.getDate() - dow))
}

export function isOpen(t: Task): boolean {
  return (OPEN_STATUSES as string[]).includes(t.status)
}

/**
 * A task is *pending* in the Minister Reminder Queue when it's flagged for the
 * Minister, still open, and he hasn't acted on it yet (no outcome recorded).
 * Recording any outcome removes it from the pending queue — the task keeps its
 * own status and is closed separately by the office when actually finished.
 */
export function isMinisterPending(t: Task): boolean {
  return t.ministerQueue && isOpen(t) && !t.ministerOutcome
}

/**
 * Overdue = the end date (deadline) has already passed and the task is still
 * open. A task with no end date is never overdue.
 */
export function isOverdue(t: Task): boolean {
  if (!t.endDate || !isOpen(t)) return false
  return new Date(t.endDate).getTime() < startOfDay(new Date())
}

export function isDueToday(t: Task): boolean {
  if (!t.startDate) return false
  return startOfDay(new Date(t.startDate)) === startOfDay(new Date())
}

export function checklistPct(t: Task): number {
  if (t.checklist.length === 0) return 0
  return Math.round((t.checklist.filter((c) => c.done).length / t.checklist.length) * 100)
}

/** A task is blocked when it has an incomplete dependency. */
export function isBlocked(t: Task, byId: Map<string, Task>): boolean {
  return t.dependencyIds.some((id) => {
    const dep = byId.get(id)
    return dep ? dep.status !== 'completed' && dep.status !== 'cancelled' : false
  })
}

export function reminderDue(t: Task): boolean {
  return !!t.reminderAt && isOpen(t) && new Date(t.reminderAt).getTime() <= Date.now()
}

/**
 * Timeline filter buckets shown as a button bar on the list page. Mutually
 * exclusive (a task matches exactly one), keyed off start date, calendar-based
 * (weeks run Mon-Sun). Overdue open tasks are pinned into view separately by
 * the caller regardless of which bucket is active, so nothing urgent
 * disappears — these predicates only decide "when is it scheduled".
 */
export type Timeframe =
  | 'all'
  | 'last_week'
  | 'yesterday'
  | 'today'
  | 'tomorrow'
  | 'this_week'
  | 'next_week'
  | 'this_month'
  | 'next_month'

export function matchesTimeframe(t: Task, tf: Timeframe): boolean {
  if (tf === 'all') return true
  if (!t.startDate) return false
  const when = startOfDay(new Date(t.startDate))
  const today = startOfDay(new Date())
  const yesterday = today - DAY
  const tomorrow = today + DAY
  const thisWeekStart = startOfWeek(today)
  const lastWeekStart = thisWeekStart - 7 * DAY
  const nextWeekStart = thisWeekStart + 7 * DAY
  const afterNextWeekStart = nextWeekStart + 7 * DAY
  const now = new Date()
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime()
  const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime()
  const monthAfterNextStart = new Date(now.getFullYear(), now.getMonth() + 2, 1).getTime()
  switch (tf) {
    case 'last_week':
      return when >= lastWeekStart && when < thisWeekStart
    case 'yesterday':
      return when === yesterday
    case 'today':
      return when === today
    case 'tomorrow':
      return when === tomorrow
    case 'this_week':
      // True calendar week, Monday through Sunday — includes today and any
      // already-passed days earlier this week, not just what's still ahead.
      return when >= thisWeekStart && when < nextWeekStart
    case 'next_week':
      return when >= nextWeekStart && when < afterNextWeekStart
    case 'this_month':
      // Rest of the current month not already covered by This/Next Week.
      return when >= Math.max(afterNextWeekStart, thisMonthStart) && when < nextMonthStart
    case 'next_month':
      return when >= nextMonthStart && when < monthAfterNextStart
    default:
      return true
  }
}

/** Quick-filter predicate used by the list page. Date filters key off start date. */
export function matchesFilter(t: Task, filter: FilterId, currentUserId: string): boolean {
  const today = startOfDay(new Date())
  const when = t.startDate ? startOfDay(new Date(t.startDate)) : null
  switch (filter) {
    case 'all':
      return true
    case 'today':
      return when === today
    case 'tomorrow':
      return when === today + DAY
    case 'this_week':
      return when !== null && when >= today && when <= today + 6 * DAY
    case 'overdue':
      return isOverdue(t)
    case 'pending':
      return isOpen(t)
    case 'completed':
      return t.status === 'completed'
    case 'assigned_to_me':
      return t.assignedToId === currentUserId
    case 'high_priority':
      return t.priority === 'critical' || t.priority === 'high'
    case 'g_communication':
      return categoryGroup(t.category) === 'communication'
    case 'g_meeting_prep':
      return categoryGroup(t.category) === 'meeting_prep'
    case 'g_administrative':
      return categoryGroup(t.category) === 'administrative'
    case 'g_personal_reminder':
      return categoryGroup(t.category) === 'personal_reminder'
    default:
      return true
  }
}

/**
 * "Available Now" selection: greedily fit open tasks (with an estimate) into the
 * available minutes, favouring higher priority then shorter tasks.
 */
export function suggestForTime(tasks: Task[], minutes: number): { picks: Task[]; total: number } {
  const candidates = tasks
    .filter((t) => isOpen(t) && t.estimatedMinutes && t.estimatedMinutes > 0)
    .sort((a, b) => {
      const p = PRIORITY_META[a.priority].order - PRIORITY_META[b.priority].order
      if (p !== 0) return p
      return (a.estimatedMinutes ?? 0) - (b.estimatedMinutes ?? 0)
    })
  const picks: Task[] = []
  let total = 0
  for (const t of candidates) {
    const est = t.estimatedMinutes ?? 0
    if (total + est <= minutes) {
      picks.push(t)
      total += est
    }
  }
  return { picks, total }
}
